import {
  Container,
  Step,
  StepLabel,
  Stepper,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import RdCampaignStep1 from "./RdCampaignStep1";
import RdCampaignStep2 from "./RdCampaignStep2";
import RdCampaignStep3 from "./RdCampaignStep3";
import useApiEp from "../../hooks/useApiEp";
import { FaArrowLeft, FaPaperPlane, FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  getErrorResponse,
  getHeader,
  setTemplatesList,
  synContactsList,
  toastError,
} from "../../utils/util";
import LoadingWidget from "../../widgets/LoadingWidget";
import { API_ENDPOINT } from "../../utils/constants";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useSearchParams } from "react-router-dom";
import QualityRatingsMessage from "./RdQualtityRatingsMessage";
import { useDialog } from "../../context/DialogContext";

const STEPPER_ITEMS = [
  "Settings & Template",
  "Target Audience",
  "Review and Broadcast",
];

const getStepIcon = (index) => {
  switch (index) {
    case 0:
      return <DescriptionIcon />;
    case 1:
      return <SettingsIcon />;
    case 2:
      return <CheckCircleIcon />;
    default:
      return <DescriptionIcon />;
  }
};

const AnimatedStep = ({ active, children }) => {
  return (
    <motion.div
      initial={{ scale: 1 }}
      animate={{ scale: active ? 1.1 : 1 }}
      transition={{ duration: 0.3 }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: active ? "#1976d2" : "rgba(0, 0, 0, 0.38)",
      }}
    >
      {children}
    </motion.div>
  );
};

// Liquid glass button component
const LiquidGlassButton = ({ children, variant = "contained", ...props }) => {
  const baseStyles = {
    position: "relative",
    overflow: "hidden",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    background:
      variant === "contained"
        ? "linear-gradient(135deg, rgba(120, 119, 198, 0.8), rgba(85, 83, 230, 0.6))"
        : "rgba(255, 255, 255, 0.1)",
    color: variant === "contained" ? "white" : "inherit",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
      transition: "left 0.5s",
    },
    "&:hover::before": {
      left: "100%",
    },
    "&:hover": {
      background:
        variant === "contained"
          ? "linear-gradient(135deg, rgba(140, 139, 218, 0.9), rgba(105, 103, 250, 0.7))"
          : "rgba(255, 255, 255, 0.2)",
      boxShadow: "0 8px 32px rgba(120, 119, 198, 0.3)",
      transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
    boxShadow: "0 4px 20px rgba(120, 119, 198, 0.2)",
  };

  return (
    <Button
      {...props}
      variant={variant}
      sx={{
        ...baseStyles,
        ...props.sx,
      }}
    >
      {children}
    </Button>
  );
};

const RdCampaign = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const {
    data: PhoneData,
    isLoading: isPhoneLoading,
    isError: isPhoneError,
    error: phoneError,
  } = useApiEp(`/user/phone-details`);
  const { showDialog } = useDialog();
  const tResponseD = useApiEp(`/api/get-templates-sync`);
  const [scheduledDate, setScheduledDate] = useState();
  const [contactGroup, setContactGroup] = useState(0);
  const [contactGroupName, setContactGroupName] = useState("");
  const [campaignGroupName, setCampaignGroupName] = useState("");
  const [contactsType, setContactsType] = useState("contacts");
  const [checkingStep, setCheckingStep] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [campaignPost, setCampaignPost] = useState({
    campaignName: "",
    header: {},
    templateId: "",
    bodyParams: [],
    template: null,
    contacts: [],
    cards: [],
    splitAcrossAccounts: false,
  });
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeStep]);
  useEffect(() => {
    const paramValue = searchParams.get("gn");
    const ctype = searchParams.get("contactsType");
    setContactGroup(["1", "2"].includes(paramValue) ? Number(paramValue) : 0);
    setContactGroupName(searchParams.get("name"));
    setCampaignGroupName(searchParams.get("groupName"));
    setContactsType(ctype);
  }, [searchParams]);

  useEffect(() => {
    if (scheduledDate) {
      setCampaignPost((prev) => ({ ...prev, scheduledDate }));
    }
  }, [scheduledDate]);
  async function doCampaign(autoSplit = false) {
    if (campaignPost.contacts.length < 1) {
      toast.error("Please select contacts for campaign");
      return;
    }
    const isStatic =
      campaignPost?.isStaticParams && campaignPost.bodyParams.length > 0;
    if (isStatic) {
      const checkP = campaignPost.bodyParams.filter((item) => item === "");
      if (checkP.length > 0) {
        toast.error("Please ensure you have entered all static parameters");
        return;
      }
    }
    if (!campaignPost.template) {
      toast.error("Please select template for campaign");
      return;
    }
    if (campaignPost?.isCampaignScheduled && !scheduledDate) {
      toast.error("Please specify scheduled date");
      return;
    }
    if (campaignPost?.smartRetry && !campaignPost?.retryIntervals) {
      toast.error("Please specify retry intervals");
      return;
    }
    const payload = {
      campaignName: campaignPost.campaignName,
      templateId: campaignPost.template.id,
      header: campaignPost.header,
      ...(autoSplit ? { autoSplit: true } : {}),
      ...(campaignPost?.useDefaultMedia ? { useDefaultMedia: true } : {}),
    };
    if (campaignPost.isCarousel) {
      payload.cards = campaignPost.cards;
    }
    if ("contactMapping" in campaignPost) {
      payload.contactMapping = campaignPost.contactMapping;
    }

    if ("headerMapping" in campaignPost) {
      payload.headerMapping = campaignPost.headerMapping;
    }
    if (campaignPost.isMarketing) {
      payload.isMarketing = true;
      payload.categoryId = campaignPost.contactGroup._id;
      payload.indexStart = {
        start: campaignPost.startIndex,
        end: campaignPost.endIndex,
      };
    } else {
      payload.contacts = campaignPost.contacts;
    }
    if (isStatic) {
      payload.bodyParams = campaignPost.bodyParams;
    }
    if (campaignPost?.isCampaignScheduled) {
      payload.isCampaignScheduled = true;
      payload.scheduledDate = scheduledDate;
    }
    if (campaignPost?.marketingQuery) {
      payload.marketingQuery = campaignPost.marketingQuery;
    }
    if (campaignPost?.smartRetry) {
      payload.smartRetry = campaignPost.smartRetry;
      payload.retryIntervals = campaignPost.retryIntervals;
    }
    if (campaignPost?.splitAccounts) {
      payload.splitAccounts = campaignPost.splitAccounts;
    }
    if (campaignPost?.urlParams) {
      payload.urlParams = campaignPost.urlParams;
    }
    if (campaignPost?.catalogueThumbnail) {
      payload.catalogueThumbnail = campaignPost.catalogueThumbnail;
    }
    if (campaignPost?.contactGroupSelected) {
      payload.contactGroupSelected = true;
      payload.contactGroup = campaignPost.contactGroup;
    }
    if (campaignGroupName) {
      payload.campaignGroupName = campaignGroupName;
    }
    if (campaignPost?.isMainAccountExcluded) {
      payload.isMainAccountExcluded = campaignPost.isMainAccountExcluded;
    }

    try {
      setSendingCampaign(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/create-campaign`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      navigate(`/home/queue-monitor`);
    } catch (error) {
      showDialog("Error", getErrorResponse(error), {
        type: "error",
        onConfirm: () => {},
      });
    } finally {
      setSendingCampaign(false);
    }
  }

  const handleCreateCampaign = useCallback(async () => {
    if (activeStep === 0) {
      if (campaignPost.isCarousel) {
        if (
          !campaignPost?.useDefaultMedia &&
          campaignPost?.cards?.length !== campaignPost.carouselMediaCount
        ) {
          toast.error("Please select media items as per the carousel cards!");
          return;
        }
      }

      try {
        setCheckingStep(true);
        const response = await axios.post(
          `${API_ENDPOINT}/campaign/check-campaign-name`,
          { campaignName: campaignPost.campaignName },
          getHeader()
        );
        if (!response.data.exists) {
          return;
        }
      } catch (error) {
        toastError(error);
        return;
      } finally {
        setCheckingStep(false);
      }
    }
    if (activeStep < 2) {
      if (activeStep === 1) {
        if (campaignPost.contacts.length < 1) {
          toast.error("Please add contacts to target audience for campaign");
          return;
        }
      }
      const splitCount = (campaignPost.splitAccounts ?? []).length;
      if (
        campaignPost.template &&
        ((splitCount > 0 && campaignPost?.isMainAccountExcluded) ||
          campaignPost.template.status === "APPROVED")
      ) {
        setCampaignPost((prev) => ({ ...prev, contactGroupSelected: true }));
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else {
        toast.error("Please select an approved template to proceed.");
      }
    }

    if (activeStep === 2) {
      if (
        campaignPost?.dataSplitsList &&
        campaignPost.dataSplitsList.length > 0
      ) {
        showDialog(
          "Please confirm!",
          "Are you sure you want do this campaign?",
          {
            type: "warning",
            onConfirm: () => {
              doCampaign(true);
            },
          }
        );
      } else {
        doCampaign(false);
      }
    }
  }, [activeStep, campaignPost]);

  if (tResponseD.isLoading) {
    return (
      <Box m={4}>
        <LoadingWidget
          label="Loading Templates..."
          type="circular"
          thickness={6}
          size={90}
        />
      </Box>
    );
  }
  if (tResponseD?.isError) {
    return (
      <Box m={3}>
        <Alert severity="error">{getErrorResponse(tResponseD.error)}</Alert>
      </Box>
    );
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <RdCampaignStep1
            qualityRating={
              PhoneData?.data?.quality_rating ||
              PhoneData?.data?.quality_score?.score
            }
            templateD={tResponseD.data.data}
            scheduledDate={scheduledDate}
            setScheduledDate={setScheduledDate}
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
          />
        );
      case 1:
        return (
          <RdCampaignStep2
            qualityRating={PhoneData?.data?.quality_rating}
            setActiveStep={setActiveStep}
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
            contactGroup={contactGroup}
            setContactGroup={setContactGroup}
            contactGroupName={contactGroupName}
            contactsType={contactsType}
          />
        );
      case 2:
        return (
          <RdCampaignStep3
            qualityRating={PhoneData?.data?.quality_rating}
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
            onConfirmSplit={() => {
              doCampaign(true);
            }}
          />
        );
    }
  };

  if (
    PhoneData &&
    PhoneData?.data?.quality_rating !== undefined &&
    PhoneData?.data?.quality_rating !== "GREEN" &&
    PhoneData?.data?.quality_rating !== "UNKNOWN" &&
    PhoneData?.data?.quality_score?.score !== "GREEN" &&
    PhoneData?.data?.quality_score?.score !== "UNKNOWN"
  ) {
    return (
      <Box m={3}>
        <QualityRatingsMessage
          qualityRating={
            PhoneData?.data?.quality_rating ||
            PhoneData?.data?.quality_score?.score
          }
        />
      </Box>
    );
  }

  return (
    <Container
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        py: 3,
      }}
    >
      {/* Animated background elements */}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPPER_ITEMS.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={(props) => (
                    <AnimatedStep active={props.active || props.completed}>
                      <Box
                        sx={{
                          background:
                            props.active || props.completed
                              ? "linear-gradient(135deg, rgba(120,119,198,0.9), rgba(85,83,230,0.7))"
                              : "rgba(255, 255, 255, 0.3)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          borderRadius: "50%",
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color:
                            props.active || props.completed
                              ? "white"
                              : "rgba(0, 0, 0, 0.38)",
                          boxShadow: "0 4px 20px rgba(120, 119, 198, 0.3)",
                        }}
                      >
                        {getStepIcon(index)}
                      </Box>
                    </AnimatedStep>
                  )}
                >
                  <motion.div
                    initial={{ color: "#0000008a" }}
                    animate={{
                      color: activeStep === index ? "#1976d2" : "#0000008a",
                      fontWeight: activeStep === index ? "bold" : "normal",
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {label}
                  </motion.div>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </motion.div>

      {/* Animated content area */}
      <div ref={contentRef}>
        <motion.div
          key={activeStep}
          initial={{
            x: activeStep === 0 ? 0 : activeStep === 1 ? 100 : -100,
            opacity: 0,
          }}
          animate={{ x: 0, opacity: 1 }}
          exit={{
            x: activeStep === 0 ? -100 : activeStep === 1 ? 100 : -100,
            opacity: 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              mt: 4,
              mb: 1,
              p: 3,
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(15px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              minHeight: 200,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background:
                  "linear-gradient(90deg, transparent, rgba(120,119,198,0.5), transparent)",
              },
            }}
          >
            {getStepContent(activeStep)}
          </Box>
        </motion.div>
      </div>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pt: 3,
          mb: 3,
          position: "relative",
          background:
            "linear-gradient(90deg, rgba(251,188,5,0.1), rgba(234,67,53,0.1))",
          borderRadius: "16px",
          padding: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <LiquidGlassButton
            variant="outlined"
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<FaArrowLeft />}
            sx={{
              padding: "12px 24px",
              fontSize: "16px",
              minHeight: "48px",
              background: "transparent",
              border:
                activeStep === 0
                  ? "1.5px solid rgba(251,188,5,0.2)"
                  : "1.5px solid rgba(251,188,5,0.8)",
              backdropFilter: "blur(20px)",
              color:
                activeStep === 0
                  ? "rgba(251,188,5,0.4)"
                  : "rgba(251,188,5,0.9)",
              borderRadius: "12px",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(251,188,5,0.1) 0%, transparent 50%, rgba(251,188,5,0.1) 100%)",
                opacity: 0,
                transition: "opacity 0.3s ease",
              },
              "&:hover::before": {
                opacity: activeStep === 0 ? 0 : 1,
              },
              "&:hover": {
                background:
                  activeStep === 0 ? "transparent" : "rgba(251,188,5,0.1)",
                border:
                  activeStep === 0
                    ? "1.5px solid rgba(251,188,5,0.2)"
                    : "1.5px solid rgba(251,188,5,1)",
                transform: activeStep === 0 ? "none" : "translateY(-2px)",
                boxShadow:
                  activeStep === 0 ? "none" : "0 8px 25px rgba(251,188,5,0.15)",
              },
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
          >
            Back
          </LiquidGlassButton>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeStep === STEPPER_ITEMS.length - 1 ? (
            <LoadingButton
              variant="outlined"
              loading={sendingCampaign || checkingStep}
              onClick={handleCreateCampaign}
              endIcon={<FaPaperPlane />}
              sx={{
                padding: "14px 32px",
                fontSize: "16px",
                minHeight: "52px",
                fontWeight: 600,
                position: "relative",
                overflow: "hidden",
                backdropFilter: "blur(25px)",
                background: "transparent",
                border: "1.5px solid #EA4335",
                color: "#EA4335",
                borderRadius: "14px",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(234,67,53,0.2), transparent)",
                  transition: "left 0.7s ease-in-out",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "1px",
                  left: "1px",
                  right: "1px",
                  bottom: "1px",
                  background:
                    "linear-gradient(135deg, transparent, rgba(234,67,53,0.1), transparent)",
                  borderRadius: "13px",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover::before": {
                  left: "100%",
                },
                "&:hover::after": {
                  opacity: 1,
                },
                "&:hover": {
                  background: "rgba(234,67,53,0.1)",
                  border: "1.5px solid #EA4335",
                  boxShadow: "0 12px 40px rgba(234,67,53,0.3)",
                  transform: "translateY(-3px)",
                  color: "#EA4335",
                },
                "&:active": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 25px rgba(234,67,53,0.2)",
                },
                "&.MuiLoadingButton-loading": {
                  border: "1.5px solid rgba(234,67,53,0.5)",
                  color: "rgba(234,67,53,0.5)",
                },
                transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              }}
            >
              Send Campaign
            </LoadingButton>
          ) : (
            <LiquidGlassButton
              variant="outlined"
              onClick={handleCreateCampaign}
              endIcon={<FaArrowRight />}
              sx={{
                padding: "14px 32px",
                fontSize: "16px",
                minHeight: "52px",
                fontWeight: 600,
                backdropFilter: "blur(20px)",
                background: "transparent",
                border: "1.5px solid rgba(234,67,53,0.8)",
                color: "rgba(234,67,53,0.9)",
                borderRadius: "12px",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(234,67,53,0.2), transparent)",
                  transition: "left 0.6s ease-in-out",
                },
                "&:hover::before": {
                  left: "100%",
                },
                "&:hover": {
                  background: "rgba(234,67,53,0.1)",
                  border: "1.5px solid #EA4335",
                  boxShadow: "0 12px 35px rgba(234,67,53,0.2)",
                  transform: "translateY(-2px)",
                  color: "#EA4335",
                },
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              }}
            >
              Next
            </LiquidGlassButton>
          )}
        </motion.div>
      </Box>
    </Container>
  );
};

export default RdCampaign;
